/**
 * Settings → Factory manual.
 *
 * The plant keeps its own manual in Excel: sections, the person who runs each
 * one, and the materials in and out. Upload it and the application takes its
 * shape from it — materials it does not have are created, every machine in a
 * section gets that section's lists, and the people named get a login.
 *
 * Nothing is written until the preview has been read. The passwords for any
 * login it creates are shown once, here, and never again.
 */

import { useState } from "react";
import { ApiError } from "../../lib/api";
import {
  uploadFactoryManual,
  type FactoryManualPlan,
} from "../../lib/controls";

export function FactoryManualPanel() {
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<FactoryManualPlan | null>(null);
  const [withLogins, setWithLogins] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(mode: "preview" | "commit", chosen?: File) {
    const target = chosen ?? file;
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const result = await uploadFactoryManual(target, mode, withLogins);
      setPlan(result);
      setFile(target);
    } catch (e) {
      setError((e as ApiError).message);
      setPlan(null);
    } finally {
      setBusy(false);
    }
  }

  const created = plan?.saved ? plan.logins.filter((l) => l.password) : [];

  return (
    <>
      {error && <div className="banner banner--negative" role="alert">{error}</div>}

      <section className="card">
        <h3 className="section-title">Factory manual</h3>
        <p className="section-note">
          One row per material, under a section heading, with the person who
          runs that section and the columns Input and Output — the sheet the
          plant already keeps. A fifth column, Waste, is read if it is there;
          without it a machine offers every material as waste. Every machine
          in a section takes that section's lists. Materials that are new are
          created; materials the master already holds under another name are
          reused, so stock does not split in two. Upload shows what it would
          do before anything is saved.
        </p>

        <div className="page__footer">
          <label className="inputgroup">
            <span className="label">Choose the manual (.xlsx or .csv)</span>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => {
                const chosen = e.target.files?.[0];
                e.target.value = "";
                if (chosen) run("preview", chosen);
              }}
            />
          </label>
          <label className="inputgroup">
            <span className="label">People named in it</span>
            <select
              value={withLogins ? "yes" : "no"}
              onChange={(e) => setWithLogins(e.target.value === "yes")}
            >
              <option value="yes">Create a login for each</option>
              <option value="no">Ignore them</option>
            </select>
          </label>
        </div>

        {busy && <div className="skeleton state__bar" />}

        {plan && (
          <>
            {plan.errors.length > 0 && (
              <div className="banner banner--negative">
                <div>
                  <strong>Nothing was saved.</strong>
                  <ul className="banner__list">
                    {plan.errors.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {plan.saved && (
              <div className="banner banner--positive">
                <div>
                  <strong>
                    Loaded: {plan.new_items.length} new materials,{" "}
                    {Object.keys(plan.machines).length} machines,{" "}
                    {created.length} new logins.
                  </strong>
                </div>
              </div>
            )}

            {plan.warnings.length > 0 && (
              <div className="banner banner--warning">
                <ul className="banner__list">
                  {plan.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </div>
            )}

            {created.length > 0 && (
              <div className="banner banner--warning">
                <div>
                  <strong>
                    Write these passwords down now — they are not shown again.
                  </strong>
                  <ul className="banner__list">
                    {created.map((l) => (
                      <li key={l.email}>
                        {l.name} · {l.email} · <span className="mono">{l.password}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="page__note">
                    Each person should change it under their own name in the
                    top bar the first time they sign in.
                  </span>
                </div>
              </div>
            )}

            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Machines it sets</th>
                    <th>Runs it</th>
                    <th className="num">Inputs</th>
                    <th className="num">Outputs</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.sections.map((s) => (
                    <tr key={s.section}>
                      <td>{s.section}</td>
                      <td>{s.machines.join(", ") || <span className="cell__unit">not a stage here</span>}</td>
                      <td>{s.users.join(", ") || "—"}</td>
                      <td className="num mono">{s.inputs}</td>
                      <td className="num mono">{s.outputs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {plan.machines_untouched.length > 0 && (
              <p className="section-note">
                Not in the manual, so left exactly as they are:{" "}
                {plan.machines_untouched.join(", ")}.
              </p>
            )}

            {plan.existing_logins.length > 0 && (
              <p className="section-note">
                Already has a login: {plan.existing_logins.join(", ")}.
              </p>
            )}

            {plan.new_items.length > 0 && (
              <>
                <h4 className="section-title">
                  {plan.saved ? "Materials created" : "Materials it would create"}
                </h4>
                <div className="chipset">
                  {plan.new_items.map((i) => (
                    <span key={i.code}
                          className={`chip${i.guessed ? "" : " chip--on"}`}
                          title={`${i.name} · ${i.item_type.toLowerCase().replace("_", " ")}`}>
                      {i.code}
                    </span>
                  ))}
                </div>
              </>
            )}

            {!plan.saved && plan.errors.length === 0 && (
              <div className="page__footer">
                <button className="btn btn--auto" disabled={busy}
                        onClick={() => run("commit")}>
                  Load this manual
                </button>
                <button className="btn btn--ghost btn--auto"
                        onClick={() => setPlan(null)}>
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
